import { useRef, useCallback, useEffect, useState } from 'react'
import Handsontable from 'handsontable/base'
import { testInstanceHolder } from '@utils/test-instance-holder'
import { defaultOptions } from '../config'
import type { GridSettings } from 'handsontable/settings'
import { registerPlugin, CopyPaste } from 'handsontable/plugins'

registerPlugin(CopyPaste)

interface IHandsontableHookReturn {
  handsontableInstance: Handsontable | null
  isReady: boolean
  getHandsontableInstance: () => Handsontable | null
  getHotTableContainerRef: () => HTMLElement | null
}

interface IHandsontableHookArgs {
  setParentComponentRef: (node: Handsontable | null) => void
  handsonTableOptions: GridSettings
  onTableClick: (event, ref) => void
}

interface HSEventListener {
  eventManager: {
    clear: () => void
  }
}

/**
 * Set Handsontable instance and options.
 * - Set ready flag for Handsontable Binder setup next.
 * - Set parent component ref.
 * @param hookArgs
 * @param hookArgs.setParentComponentRef Set the ref in a parent component to access Ref.
 * @param hookArgs.handsonTableOptions Handsontable options set outside the responsibilities of this hook
 * @param hookArgs.onTableClick Handsontable table click handling outside the responsibilities of this hook.
 * @returns
 */
export function useHandsontable({
  setParentComponentRef,
  handsonTableOptions,
  onTableClick,
}: IHandsontableHookArgs): IHandsontableHookReturn {
  const domTableContainerRef = useRef<HTMLElement | null>(null)
  const handsontableInstance = useRef<Handsontable | null>(null)
  const [isReady, setIsReady] = useState(false) // important that a refresh is triggered after setup.
  const $hotTableContainerRef = domTableContainerRef.current

  function cleanup() {
    setParentComponentRef(null)
    setIsReady(false)
    $hotTableContainerRef?.removeEventListener('click', onTableClickHandler, false)
    handsontableInstance.current?.updateSettings({ fillHandle: false })
    const listeners = (
      handsontableInstance.current as unknown as { eventListeners?: HSEventListener[] }
    ).eventListeners
    listeners?.[0]?.eventManager.clear()
    handsontableInstance.current?.unlisten()
    handsontableInstance.current?.destroy()
  }

  function onTableClickHandler(event) {
    onTableClick(event, $hotTableContainerRef)
  }

  function getHandsontableInstance() {
    return handsontableInstance.current
  }

  function getHotTableContainerRef() {
    return domTableContainerRef.current
  }

  const setDomTableRefCallback = useCallback(node => {
    if ($hotTableContainerRef) cleanup()

    if (node) {
      const { width, height } = node?.getBoundingClientRect() || {
        width: 0,
        height: 0,
      }

      const hotTableOptions: GridSettings & { copyPaste: { uiContainer: HTMLElement } } = {
        //TODO: the types are wrong for handsontable 13.1, revist this override after upgrading
        ...defaultOptions,
        ...handsonTableOptions,
        width,
        height,
        copyPaste: {
          uiContainer: node,
        },
      }

      const hotTable = new Handsontable(node, hotTableOptions)

      testInstanceHolder.register('submission-table', hotTable)

      setHandsontableInstance(hotTable)
      setParentComponentRef(hotTable)

      setIsReady(true)

      node?.addEventListener('click', onTableClickHandler, false)
    }

    domTableContainerRef.current = node
  }, [])

  const setHandsontableInstance = useCallback(node => {
    handsontableInstance.current = node
  }, [])

  useEffect(() => {
    setDomTableRefCallback(document.getElementById('handsontable-container'))

    return () => {
      cleanup()
    }
  }, [])

  return {
    getHandsontableInstance,
    getHotTableContainerRef,
    handsontableInstance: handsontableInstance.current,
    isReady,
  }
}
